import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save, RotateCcw } from 'lucide-react';

type Rules = {
  id: string;
  is_active: boolean;
  tier_super_hot_weight: number;
  tier_hot_weight: number;
  tier_standard_weight: number;
  freshness_weight: number;
  freshness_decay_days: number;
  quality_weight: number;
  popularity_weight: number;
  popularity_cap: number;
  stale_penalty: number;
  stale_after_days: number;
  relevance_cap: number;
  tier_min_decay: number;
  tier_decay_days: number;
  notes: string | null;
  updated_at: string;
};

const DEFAULTS: Omit<Rules, 'id' | 'updated_at' | 'notes'> = {
  is_active: true,
  tier_super_hot_weight: 500,
  tier_hot_weight: 300,
  tier_standard_weight: 120,
  freshness_weight: 150,
  freshness_decay_days: 14,
  quality_weight: 0.8,
  popularity_weight: 10,
  popularity_cap: 20,
  stale_penalty: 50,
  stale_after_days: 60,
  relevance_cap: 250,
  tier_min_decay: 0.3,
  tier_decay_days: 30,
};

export function SearchRankingRulesManager() {
  const { toast } = useToast();
  const [rules, setRules] = useState<Rules | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from('search_ranking_rules')
      .select('*')
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) toast({ title: 'Failed to load rules', description: error.message, variant: 'destructive' });
    setRules(data || null);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!rules) return;
    setSaving(true);
    const { id, updated_at, ...payload } = rules;
    const { error } = await (supabase as any)
      .from('search_ranking_rules')
      .update(payload)
      .eq('id', id);
    setSaving(false);
    if (error) {
      toast({ title: 'Save failed', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Ranking rules saved', description: 'Search results will use the new weights immediately.' });
      load();
    }
  };

  const reset = () => {
    if (!rules) return;
    setRules({ ...rules, ...DEFAULTS });
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  if (!rules) return <div className="p-8 text-muted-foreground">No ranking rules row found.</div>;

  const setNum = (k: keyof Rules) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setRules({ ...rules, [k]: Number(e.target.value) } as Rules);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-bold">Search Ranking Rules</h1>
        <p className="text-muted-foreground text-sm">
          Tune how search results are scored. Tier boosts are only applied while a featured listing is within its active window;
          after expiration, listings fall back to normal sorting automatically.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Active</span>
            <Switch checked={rules.is_active} onCheckedChange={(v) => setRules({ ...rules, is_active: v })} />
          </CardTitle>
          <CardDescription>When off, search falls back to built-in defaults.</CardDescription>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader><CardTitle>Tier weights (active boost only)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label>Super Hot</Label><Input type="number" value={rules.tier_super_hot_weight} onChange={setNum('tier_super_hot_weight')} /></div>
          <div><Label>Hot</Label><Input type="number" value={rules.tier_hot_weight} onChange={setNum('tier_hot_weight')} /></div>
          <div><Label>Standard</Label><Input type="number" value={rules.tier_standard_weight} onChange={setNum('tier_standard_weight')} /></div>
          <div><Label>Tier min decay (0-1)</Label><Input type="number" step="0.05" value={rules.tier_min_decay} onChange={setNum('tier_min_decay')} /></div>
          <div><Label>Tier decay window (days)</Label><Input type="number" value={rules.tier_decay_days} onChange={setNum('tier_decay_days')} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Quality / freshness / popularity</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label>Freshness weight</Label><Input type="number" value={rules.freshness_weight} onChange={setNum('freshness_weight')} /></div>
          <div><Label>Freshness decay (days)</Label><Input type="number" value={rules.freshness_decay_days} onChange={setNum('freshness_decay_days')} /></div>
          <div><Label>Quality multiplier</Label><Input type="number" step="0.1" value={rules.quality_weight} onChange={setNum('quality_weight')} /></div>
          <div><Label>Popularity weight</Label><Input type="number" value={rules.popularity_weight} onChange={setNum('popularity_weight')} /></div>
          <div><Label>Popularity cap</Label><Input type="number" value={rules.popularity_cap} onChange={setNum('popularity_cap')} /></div>
          <div><Label>Relevance cap</Label><Input type="number" value={rules.relevance_cap} onChange={setNum('relevance_cap')} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Stale penalty</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Penalty (points)</Label><Input type="number" value={rules.stale_penalty} onChange={setNum('stale_penalty')} /></div>
          <div><Label>Stale after (days)</Label><Input type="number" value={rules.stale_after_days} onChange={setNum('stale_after_days')} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Notes</CardTitle></CardHeader>
        <CardContent>
          <Textarea value={rules.notes || ''} onChange={(e) => setRules({ ...rules, notes: e.target.value })} placeholder="Why these values?" />
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button onClick={save} disabled={saving}><Save className="h-4 w-4 mr-2" />{saving ? 'Saving…' : 'Save rules'}</Button>
        <Button variant="outline" onClick={reset}><RotateCcw className="h-4 w-4 mr-2" />Reset to defaults</Button>
      </div>
    </div>
  );
}

export default SearchRankingRulesManager;
